import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import type { GameStateSnapshot } from './gameStateSync';

export type ResyncCallback = () => void;
export type ResyncRequestCallback = () => void;

export class ResyncManager {
  private overlay: Container;
  private syncingText: Text;
  private isResyncing: boolean = false;
  private resyncTimeout: number | null = null;
  private readonly RESYNC_TIMEOUT_BASE = 3000;
  private readonly MAX_RETRIES = 3;
  private retryCount: number = 0;
  private onResyncComplete: ResyncCallback | null = null;
  private onResyncRequest: ResyncRequestCallback | null = null;
  private lastReceivedState: GameStateSnapshot | null = null;

  constructor(screenWidth: number, screenHeight: number) {
    this.overlay = new Container();
    this.overlay.visible = false;

    const padding = 10;
    const boxWidth = 120;
    const boxHeight = 40;
    const x = screenWidth - boxWidth - padding;
    const y = screenHeight - boxHeight - padding;

    const background = new Graphics();
    background.roundRect(x, y, boxWidth, boxHeight, 8);
    background.fill({ color: 0xff9800, alpha: 0.9 });
    background.stroke({ width: 2, color: 0xff6f00 });
    this.overlay.addChild(background);

    const textStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 14,
      fill: '#ffffff',
      fontWeight: 'bold',
    });

    this.syncingText = new Text({ text: '⟳ Syncing...', style: textStyle });
    this.syncingText.anchor.set(0.5, 0.5);
    this.syncingText.position.set(x + boxWidth / 2, y + boxHeight / 2);
    this.overlay.addChild(this.syncingText);
  }

  getOverlay(): Container {
    return this.overlay;
  }

  startResync(): void {
    if (this.isResyncing) return;

    this.isResyncing = true;
    this.retryCount = 0;
    this.overlay.visible = true;
    this.syncingText.text = '⟳ Syncing...';

    this.sendRequestAndScheduleTimeout();
  }

  private sendRequestAndScheduleTimeout(): void {
    if (this.onResyncRequest) {
      this.onResyncRequest();
    }

    if (this.resyncTimeout !== null) {
      clearTimeout(this.resyncTimeout);
    }

    // Exponential backoff: 3s, 6s, 12s
    const timeout = this.RESYNC_TIMEOUT_BASE * Math.pow(2, this.retryCount);

    this.resyncTimeout = window.setTimeout(() => {
      this.resyncTimeout = null;

      if (this.retryCount < this.MAX_RETRIES) {
        this.retryCount++;
        console.warn(`[Resync] Timeout, retrying (${this.retryCount}/${this.MAX_RETRIES})...`);
        this.syncingText.text = `⟳ Retry ${this.retryCount}/${this.MAX_RETRIES}...`;
        this.sendRequestAndScheduleTimeout();
      } else {
        console.warn('[Resync] Max retries reached, force-completing resync');
        this.completeResync();
      }
    }, timeout);
  }

  receiveState(state: GameStateSnapshot): void {
    // Only store state when actively resyncing to avoid holding stale references
    if (this.isResyncing) {
      this.lastReceivedState = state;
      this.completeResync();
    }
  }

  private completeResync(): void {
    if (this.resyncTimeout !== null) {
      clearTimeout(this.resyncTimeout);
      this.resyncTimeout = null;
    }

    this.isResyncing = false;
    this.retryCount = 0;
    this.overlay.visible = false;

    if (this.onResyncComplete) {
      this.onResyncComplete();
    }
  }

  isActive(): boolean {
    return this.isResyncing;
  }

  setResyncCompleteCallback(callback: ResyncCallback): void {
    this.onResyncComplete = callback;
  }

  setResyncRequestCallback(callback: ResyncRequestCallback): void {
    this.onResyncRequest = callback;
  }

  getLastReceivedState(): GameStateSnapshot | null {
    return this.lastReceivedState;
  }
}
